'use client';

import Link from 'next/link';
import { ArrowRight, AlertCircle, ShieldAlert, Clock } from 'lucide-react';
import { useState, useEffect, useActionState } from 'react';
import { authenticate } from '@/app/lib/actions';

/* ─── ISPIsotipoLarge: Fidelidad Total al Sistema Nativo ────────────────── */
function ISPIsotipoLarge() {
  const nodes = [
    { accent: false, svg: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg> },
    { accent: true,  svg: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
    { accent: false, svg: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg> },
    { accent: true,  svg: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg> },
    { accent: false, svg: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg> },
    { accent: true,  svg: <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><path d="M8.5 15h3a1.5 1.5 0 0 1 0 3h-3a1.5 1.5 0 0 0 0 3h3"/></svg> },
    { accent: false, svg: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg> },
    { accent: true,  svg: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg> },
  ];

  return (
    <div
      style={{
        '--icon-r': '48px',
        '--node-s': '25px',
        '--center-s': '48px',
      } as React.CSSProperties}
      className="isp-icon"
    >
      <div className="isp-ring" style={{ borderColor: 'rgba(78, 168, 222, 0.2)' }} />
      {/* Core: servidor azul sobre círculo blanco con borde rojo */}
      <div className="isp-core" style={{ borderWidth: '3px', borderColor: 'var(--alerta)', background: 'var(--bg-surface)' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--señal)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/>
        </svg>
      </div>
      {/* 8 nodos orbitantes con sus iconos específicos */}
      {nodes.map((n, i) => (
        <div key={i} className={`isp-node n${i + 1}${n.accent ? ' accent' : ''}`}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-surface)', 
                   color: n.accent ? 'var(--alerta)' : 'var(--señal)', border: n.accent ? '2px solid var(--alerta)' : '2px solid var(--señal)' }}>
          {n.svg}
        </div>
      ))}
    </div>
  );
}

export default function LoginPage() {
  const [errorMessage, formAction, isPending] = useActionState(
    authenticate,
    undefined,
  );

  const [attempts, setAttempts] = useState(0);
  const [cooldown, setCooldown] = useState(0);

  // Cargar intentos y cooldown al montar el componente
  useEffect(() => {
    const savedAttempts = sessionStorage.getItem('failed_login_attempts');
    if (savedAttempts) {
      setAttempts(parseInt(savedAttempts, 10));
    }

    const savedLockout = sessionStorage.getItem('login_lockout_until');
    if (savedLockout) {
      const remaining = Math.ceil((parseInt(savedLockout, 10) - Date.now()) / 1000);
      if (remaining > 0) {
        setCooldown(remaining);
      }
    }
  }, []);

  // Manejar el incremento de intentos al recibir un mensaje de error nuevo
  useEffect(() => {
    if (errorMessage) {
      setAttempts((prev) => {
        const next = prev + 1;
        sessionStorage.setItem('failed_login_attempts', String(next));

        // Activar cooldown si llega a 5 intentos fallidos
        if (next >= 5) {
          const lockoutTime = Date.now() + 60000; // 60 segundos de bloqueo
          sessionStorage.setItem('login_lockout_until', String(lockoutTime));
          setCooldown(60);
        }
        return next;
      });
    }
  }, [errorMessage]);

  // Manejar el timer de la cuenta regresiva del cooldown
  useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => {
      setCooldown((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          sessionStorage.removeItem('login_lockout_until');
          setAttempts(0);
          sessionStorage.setItem('failed_login_attempts', '0');
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldown]);

  return (
    <div className="flex min-h-screen font-sans" style={{ background: 'var(--bg-base)' }}>

      {/* ─── Panel Izquierdo: Marca ───────────────────────────────────── */}
      <div
        className="hidden lg:flex w-[52%] flex-col relative overflow-hidden"
        style={{ background: 'var(--fibra)', color: 'white' }}
      >
        {/* Círculos decorativos que representan nodos de red */}
        <div
          className="absolute top-[-80px] right-[-80px] w-[420px] h-[420px] rounded-full pointer-events-none"
          style={{ border: '60px solid rgba(78, 168, 222, 0.07)' }}
        />
        <div
          className="absolute bottom-[-120px] left-[-60px] w-[360px] h-[360px] rounded-full pointer-events-none"
          style={{ border: '60px solid rgba(23, 83, 138, 0.35)' }}
        />
        <div
          className="absolute top-[35%] right-[-40px] w-[180px] h-[180px] rounded-full pointer-events-none"
          style={{ border: '30px solid rgba(206, 36, 36, 0.12)' }}
        />

        {/* Logo superior */}
        <div className="px-12 pt-10 relative z-10">
          <div className="flex items-center gap-6">
            <ISPIsotipoLarge />
            <div className="flex flex-col">
              <div className="flex items-baseline gap-0">
                <span style={{ fontSize: 36, fontWeight: 800, color: 'white', letterSpacing: '-1.5px', textShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>ISP</span>
                <span style={{ fontSize: 36, fontWeight: 600, color: 'var(--alerta)', letterSpacing: '-1.5px', textShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>Admin</span>
              </div>
              <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>
                SISTEMA DE ADMINISTRACIÓN DE ISP
              </span>
            </div>
          </div>
        </div>

        {/* Contenido central */}
        <div className="flex-1 flex flex-col justify-center px-12 lg:px-16 relative z-10">
          <span
            style={{
              background: 'rgba(23, 83, 138, 0.6)',
              border: '1px solid rgba(78, 168, 222, 0.3)',
              color: '#4EA8DE',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '1.5px',
            }}
            className="inline-block py-1 px-3 rounded-full mb-6 w-fit"
          >
            SISTEMA DE ADMINISTRACIÓN DE ISP
          </span>

          <h1
            style={{ fontWeight: 800, lineHeight: 1.15, letterSpacing: '-1.5px' }}
            className="text-4xl lg:text-5xl mb-6"
          >
            Gestiona tu red
            <br />
            con{' '}
            <span style={{ color: 'var(--torre)' }}>precisión</span>
            {' '}y{' '}
            <span style={{ color: 'var(--alerta)' }}>control</span>
          </h1>

          <p style={{ color: 'rgba(255,255,255,0.65)', fontWeight: 300, lineHeight: 1.7 }} className="text-lg max-w-md">
            Facturación, clientes, contratos, soporte y automatización de tu proveedor de internet desde una sola plataforma.
          </p>

          {/* Módulos disponibles */}
          <div className="mt-10 grid grid-cols-2 gap-3">
            {['Router', 'Clientes', 'App Móvil', 'Soporte', 'Facturación', 'Redes'].map((mod) => (
              <div
                key={mod}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
                className="flex items-center gap-2.5 px-3 py-2 rounded-lg"
              >
                <div
                  style={{ background: 'var(--señal)', width: 6, height: 6, borderRadius: '50%', flexShrink: 0 }}
                />
                <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, fontWeight: 500 }}>
                  {mod}
                </span>
              </div>
            ))}
          </div>

          {/* Testimonial / Social proof */}
          <div className="mt-10 flex items-center gap-3">
            <div className="flex -space-x-2.5">
              {[1, 2, 3].map((i) => (
                <img
                  key={i}
                  src={`https://avatar.vercel.sh/${i}isp`}
                  className="w-8 h-8 rounded-full"
                  style={{ border: '2px solid var(--fibra)' }}
                />
              ))}
              <div
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '2px solid var(--fibra)',
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 10,
                  fontWeight: 700,
                }}
                className="w-8 h-8 rounded-full flex items-center justify-center"
              >
                +99
              </div>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: 400 }}>
              Operadores confían en ISPAdmin
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-12 pb-8 relative z-10">
          <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12 }}>
            © 2026 ISPAdmin · Todos los derechos reservados
          </p>
        </div>
      </div>

      {/* ─── Panel Derecho: Formulario ─────────────────────────────────── */}
      <div
        className="flex-1 flex flex-col justify-center px-8 sm:px-14 md:px-20 lg:px-24 relative"
        style={{ background: 'var(--bg-surface)' }}
      >
        <div className="w-full max-w-[400px] mx-auto">

          {/* Logo móvil */}
          <div className="lg:hidden flex justify-center mb-8">
            <div className="flex items-center gap-2.5">
              <div
                style={{
                  '--icon-r': '22px', '--node-s': '10px', '--center-s': '18px',
                } as React.CSSProperties}
                className="isp-icon"
              >
                <div className="isp-ring" />
                <div className="isp-core"><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#17538A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg></div>
                <div className="isp-node n1" /><div className="isp-node accent n2" />
                <div className="isp-node n3" /><div className="isp-node accent n4" />
                <div className="isp-node n5" /><div className="isp-node accent n6" />
                <div className="isp-node n7" /><div className="isp-node accent n8" />
              </div>
              <div className="isp-brand" style={{ fontSize: 22 }}>
                <span className="part-isp">ISP</span>
                <span className="part-admin">Admin</span>
              </div>
            </div>
          </div>

          {/* Encabezado del form */}
          <div className="mb-8">
            <h2
              style={{ fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.5px' }}
              className="text-3xl mb-1"
            >
              Bienvenido
            </h2>
            <p style={{ color: 'var(--text-tertiary)', fontWeight: 400 }} className="text-sm">
              Ingresa tus credenciales para acceder al panel.
            </p>
          </div>

          {/* Formulario */}
          <form action={formAction} className="space-y-5">
            {/* Email */}
            <div className="space-y-1.5">
              <label
                htmlFor="email"
                style={{ color: 'var(--text-secondary)', fontWeight: 600 }}
                className="text-[13px] block"
              >
                Correo electrónico o Número de documento
              </label>
              <input
                id="email"
                type="text"
                name="email"
                placeholder="Correo o número de documento"
                required
                style={{
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-primary)',
                  borderRadius: 'var(--radius)',
                }}
                className="w-full px-4 py-2.5 text-[14px] outline-none transition-all duration-150 placeholder:text-[var(--text-muted)] focus:border-[var(--señal)] focus:shadow-[0_0_0_3px_var(--señal-light)]"
              />
            </div>

            {/* Contraseña */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  style={{ color: 'var(--text-secondary)', fontWeight: 600 }}
                  className="text-[13px]"
                >
                  Contraseña
                </label>
                <Link
                  href="#"
                  style={{ color: 'var(--señal)', fontWeight: 600 }}
                  className="text-[12px] hover:opacity-70 transition-opacity"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                name="password"
                placeholder="••••••••"
                required
                style={{
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-primary)',
                  borderRadius: 'var(--radius)',
                }}
                className="w-full px-4 py-2.5 text-[14px] outline-none transition-all duration-150 placeholder:text-[var(--text-muted)] focus:border-[var(--señal)] focus:shadow-[0_0_0_3px_var(--señal-light)]"
              />
            </div>

            {/* Error */}
            {errorMessage && (
              <div
                style={{
                  background: 'var(--danger-light)',
                  border: '1px solid var(--alerta)',
                  color: 'var(--alerta)',
                  borderRadius: 'var(--radius)',
                }}
                className="p-3 flex items-start gap-2.5 text-[13px] font-medium animate-fade-in"
              >
                <AlertCircle size={15} className="shrink-0 mt-0.5" />
                <p>
                  {(() => {
                    try {
                      const parsed = JSON.parse(errorMessage);
                      return parsed.message || parsed.error || String(errorMessage);
                    } catch {
                      return String(errorMessage);
                    }
                  })()}
                </p>
              </div>
            )}

            {/* Advertencia de intentos fallidos */}
            {attempts >= 3 && cooldown <= 0 && (
              <div
                style={{
                  background: 'rgba(245, 158, 11, 0.08)',
                  border: '1px solid #d97706',
                  color: '#b45309',
                  borderRadius: 'var(--radius)',
                }}
                className="p-3 flex items-start gap-2.5 text-[13px] font-medium animate-fade-in"
              >
                <ShieldAlert size={16} className="shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Advertencia de Seguridad</p>
                  <p className="text-[12px] opacity-90 mt-0.5 leading-relaxed text-amber-700">
                    Has realizado {attempts} intentos fallidos de inicio de sesión. Por favor, verifica tus credenciales. Si continúas fallando, tu dirección IP podría ser bloqueada temporalmente por seguridad.
                  </p>
                </div>
              </div>
            )}

            {/* Bloqueo por Cooldown */}
            {cooldown > 0 && (
              <div
                style={{
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: '1px solid var(--alerta)',
                  color: 'var(--alerta)',
                  borderRadius: 'var(--radius)',
                }}
                className="p-3 flex items-start gap-2.5 text-[13px] font-medium animate-fade-in"
              >
                <Clock size={16} className="shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Acceso bloqueado temporalmente</p>
                  <p className="text-[12px] opacity-90 mt-0.5 leading-relaxed text-red-700">
                    Demasiados intentos fallidos. Por seguridad, el acceso ha sido suspendido temporalmente. Inténtalo de nuevo en <b>{cooldown} segundos</b>.
                  </p>
                </div>
              </div>
            )}

            {/* Recordar */}
            <div className="flex items-center gap-2">
              <input
                id="remember"
                type="checkbox"
                style={{ accentColor: 'var(--señal)' }}
                className="w-4 h-4 rounded"
              />
              <label
                htmlFor="remember"
                style={{ color: 'var(--text-secondary)', fontWeight: 500 }}
                className="text-[13px] select-none cursor-pointer"
              >
                Recordarme en este equipo
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isPending || cooldown > 0}
              style={{
                background: (isPending || cooldown > 0) ? 'var(--señal-muted)' : 'var(--señal)',
                color: 'white',
                borderRadius: 'var(--radius)',
                boxShadow: (isPending || cooldown > 0) ? 'none' : '0 4px 14px rgba(23, 83, 138, 0.35)',
              }}
              className="w-full py-3 text-[14px] font-semibold flex items-center justify-center gap-2 transition-all duration-150 hover:brightness-105 active:scale-[0.99] disabled:cursor-not-allowed group"
            >
              {isPending ? 'Iniciando sesión...' : cooldown > 0 ? `Bloqueado por seguridad (${cooldown}s)` : 'Iniciar Sesión'}
              {!isPending && cooldown <= 0 && (
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform duration-150" />
              )}
            </button>
          </form>

          {/* Pie del formulario */}
          <div
            style={{ borderTop: '1px solid var(--border)' }}
            className="mt-8 pt-6 text-center"
          >
            <p style={{ color: 'var(--text-muted)' }} className="text-[13px]">
              ¿Sin acceso?{' '}
              <Link
                href="#"
                style={{ color: 'var(--señal)', fontWeight: 600 }}
                className="hover:opacity-70 transition-opacity"
              >
                Contactar soporte técnico
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
